// src/resources/dashboards/Painel360/HeatmapAiInsight.tsx
//
// Bloco "Leitura da IA" do card Movimento por hora × dia (feature 157). Contrato
// da rota em docs/superpowers/specs/2026-09-29-painel360-heatmap-leitura-ia-design.md
// §5 e §7.1 — o backend é implementado à parte, este componente só consome.
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import GroupsIcon from '@mui/icons-material/Groups';
import CampaignIcon from '@mui/icons-material/Campaign';
import InsightsIcon from '@mui/icons-material/Insights';
import dayjs from 'dayjs';
import { motion } from 'framer-motion';
import { useLocaleState } from 'react-admin';
import { AppLocale } from '../../../i18n/locale';
import { COLORS, EASE_OUT, shimmer, useDashboardSection } from './shared';
import { HourHeatmapInsightData, HourHeatmapInsightTipKind } from './types';

// Nº máximo de vezes que refaz a busca enquanto status === 'generating'. Depois
// disso o bloco some (não fica pollando pra sempre se a geração travar).
const MAX_POLLS = 10;
const DEFAULT_RETRY_SECONDS = 6;

const AI_GRADIENT = 'linear-gradient(135deg, #6366f1, #a855f7)';

// Ícone por tipo de dica — mesma cor roxa em todo o bloco (staff = escala de
// equipe, promo = push/promoção do programa, pattern = padrão da semana).
const TIP_ICON: Record<HourHeatmapInsightTipKind, React.ElementType> = {
  staff: GroupsIcon,
  promo: CampaignIcon,
  pattern: InsightsIcon,
};

const containerSx = {
  mt: 2,
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 1,
  borderRadius: 3,
  p: 1.75,
  // Gradiente índigo→roxo bem sutil — precisa se diferenciar das células azuis
  // do heatmap acima, sem competir com elas.
  background: 'linear-gradient(135deg, rgba(99,102,241,0.07), rgba(168,85,247,0.08))',
  border: '1px solid rgba(139,92,246,0.22)',
};

// Selo com o ícone de brilho — mesma leitura visual de "isto é IA" que
// CustomerVoice/ReportAI já usam em outros cantos do admin.
const Badge = () => (
  <Box sx={{
    width: 22, height: 22, borderRadius: 1.5, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: AI_GRADIENT,
  }}>
    <AutoAwesomeIcon sx={{ fontSize: 13, color: '#fff' }} />
  </Box>
);

const shimmerTextSx = {
  fontSize: 12.5,
  fontWeight: 600,
  background: 'linear-gradient(90deg, #a5b4fc 25%, #d8b4fe 50%, #a5b4fc 75%)',
  backgroundSize: '200% 100%',
  animation: `${shimmer} 1.4s ease-in-out infinite`,
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
} as const;

// Esqueleto com a identidade de IA — usado tanto no 1º carregamento quanto
// enquanto status === 'generating'.
const GeneratingSkeleton = () => (
  <Box sx={containerSx}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Badge />
      <Typography sx={shimmerTextSx}>A IA está lendo o movimento das últimas 4 semanas…</Typography>
    </Box>
  </Box>
);

interface HeatmapAiInsightProps {
  stationIds: string[];
}

const HeatmapAiInsight = ({ stationIds }: HeatmapAiInsightProps) => {
  const [localeState] = useLocaleState();
  // Só pt-br e es existem no catálogo da IA (contrato §5); qualquer outra coisa
  // (ex.: o 'en' que o react-admin usa internamente antes do i18nProvider
  // assumir) cai no pt-br, mesma regra do normalizeLocale do resto do app.
  const locale: AppLocale = localeState === 'es' ? 'es' : 'pt-br';

  // Filtro estável entre renders: stationIds chega como array novo a cada
  // render do pai (Painel360 recria o array no useMemo dele e repassa), então
  // memoiza pelo CONTEÚDO (join), não pela identidade do array — senão
  // useDashboardSection re-dispararia o fetch a cada render.
  const stationIdsKey = stationIds.join(',');
  const filter = useMemo(() => ({ stationIds, locale }), [stationIdsKey, locale]);

  const { data, loading, error, refetch } = useDashboardSection<HourHeatmapInsightData>(
    'dashboard-v2/hour-heatmap-insight',
    filter,
    true,
  );

  const [pollCount, setPollCount] = useState(0);
  const isGenerating = data?.status === 'generating';

  // Novo posto/idioma = nova análise: zera o contador de tentativas.
  useEffect(() => {
    setPollCount(0);
  }, [stationIdsKey, locale]);

  useEffect(() => {
    if (!isGenerating || pollCount >= MAX_POLLS) return;
    const retryAfter = data?.retryAfterSeconds ?? DEFAULT_RETRY_SECONDS;
    const timer = setTimeout(() => {
      setPollCount(c => c + 1);
      refetch();
    }, retryAfter * 1000);
    return () => clearTimeout(timer);
  }, [isGenerating, data?.retryAfterSeconds, pollCount, refetch]);

  if (error) return null;
  if (loading || (isGenerating && pollCount < MAX_POLLS)) return <GeneratingSkeleton />;
  if (isGenerating) return null; // esgotou as tentativas de poll — some
  if (!data || data.status === 'unavailable' || !data.insight) return null;

  const { headline, tips } = data.insight;
  const safeTips = tips ?? []; // defensivo: backend deve mandar 1–3, mas não confia
  const insightWindow = data.window;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, ease: EASE_OUT }}>
      <Box sx={containerSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Badge />
            <Typography sx={{
              fontSize: 12.5, fontWeight: 800,
              background: AI_GRADIENT,
              WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              Leitura da IA
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 10.5, color: COLORS.faint, whiteSpace: 'nowrap' }}>Últimas 4 semanas</Typography>
        </Box>

        {/* Texto da IA sempre como texto puro (children de Typography) — nunca dangerouslySetInnerHTML. */}
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: COLORS.ink }}>{headline}</Typography>

        {safeTips.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {safeTips.map((tip, i) => {
              const TipIcon = TIP_ICON[tip.kind] ?? InsightsIcon;
              return (
                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                  <TipIcon sx={{ fontSize: 14, color: '#7c3aed', mt: 0.25, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 12.5, color: '#334155' }}>{tip.text}</Typography>
                </Box>
              );
            })}
          </Box>
        )}

        {insightWindow && (
          <Typography sx={{ fontSize: 10.5, color: COLORS.faint }}>
            {`Sugestão gerada por IA com o movimento de ${dayjs(insightWindow.from).format('DD/MM')} a ${dayjs(insightWindow.to).format('DD/MM')} · atualiza toda segunda`}
          </Typography>
        )}
      </Box>
    </motion.div>
  );
};

export default HeatmapAiInsight;
